import React, { useCallback, useEffect, useState } from 'react';
import AdminReelVideoPlayer from 'components/reels/AdminReelVideoPlayer';
import MultiSelectModal from 'components/Modal/MultiSelectModal';
import { reelApprovalService, type AdminReelViewModel } from 'api/services/ReelApprovalService';
import { useReelApproval } from 'hooks/useReelApproval';

export interface AdminPendingReelsSectionProps {
  listingId?: string | null;
  title?: string;
  onActionComplete?: () => void;
}

const AdminPendingReelsSection: React.FC<AdminPendingReelsSectionProps> = ({
  listingId,
  title = 'Onay Bekleyen Gönderiler',
  onActionComplete,
}) => {
  const [reels, setReels] = useState<AdminReelViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRejectId, setPendingRejectId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { approveReel, rejectReel, loading: actionLoading, error: actionError } = useReelApproval();

  const loadReels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = listingId
        ? await reelApprovalService.getPendingByListing(listingId)
        : await reelApprovalService.getPendingStandalone();
      setReels(items);
    } catch (err) {
      setReels([]);
      setError(err instanceof Error ? err.message : 'Gönderiler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    void loadReels();
  }, [loadReels]);

  const handleApprove = async (reelId: string) => {
    const result = await approveReel(reelId);
    if (result.isSuccess) {
      setReels((prev) => prev.filter((r) => r.id !== reelId));
      onActionComplete?.();
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingRejectId) return;
    const result = await rejectReel(pendingRejectId, rejectionReason);
    if (result.isSuccess) {
      setReels((prev) => prev.filter((r) => r.id !== pendingRejectId));
      setPendingRejectId(null);
      setRejectionReason('');
      onActionComplete?.();
    }
  };

  if (!loading && reels.length === 0 && !error && listingId) {
    return null;
  }

  return (
    <div className="mb-3">
      {pendingRejectId && (
        <MultiSelectModal
          title=""
          message="Bu gönderiyi reddetmek istediğinize emin misiniz?"
          onAccept={() => void handleRejectConfirm()}
          onReject={() => {
            setPendingRejectId(null);
            setRejectionReason('');
          }}
          onDismiss={() => {
            setPendingRejectId(null);
            setRejectionReason('');
          }}
          stackOnTop
        >
          <div>
            <label htmlFor="reelRejectReason" className="form-label small text-muted mb-1">
              Reddetme gerekçesi (isteğe bağlı)
            </label>
            <textarea
              id="reelRejectReason"
              className="form-control"
              rows={3}
              maxLength={2000}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Satıcıya gösterilecek açıklama; boş bırakılırsa varsayılan metin kullanılır."
            />
          </div>
        </MultiSelectModal>
      )}

      <h6 className="text-muted text-uppercase small mb-3">
        <i className="fas fa-video me-2" aria-hidden="true" />
        {title}
      </h6>

      {loading && (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {actionError && <div className="alert alert-danger py-2">{actionError}</div>}

      {!loading && reels.length === 0 && !error && !listingId && (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-check-circle fa-2x mb-2" />
          <p className="mb-0">Onay bekleyen bağımsız gönderi bulunmuyor.</p>
        </div>
      )}

      {!loading && reels.length > 0 && (
        <div className="row g-3">
          {reels.map((reel) => (
            <div key={reel.id} className="col-12">
              <div className="card border-0 sb-surface-muted h-100">
                <div className="card-body">
                  <AdminReelVideoPlayer videoUrl={reel.videoUrl} className="mb-2" />
                  <div className="small text-muted mb-2">
                    {reel.createdAtUtc
                      ? new Date(reel.createdAtUtc).toLocaleString('tr-TR')
                      : '-'}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      disabled={actionLoading}
                      onClick={() => void handleApprove(reel.id)}
                    >
                      Onayla
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      disabled={actionLoading}
                      onClick={() => setPendingRejectId(reel.id)}
                    >
                      Reddet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPendingReelsSection;
